import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CheckCircle2, Send, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/lib/auth';
import { useBrands } from '@/lib/BrandsProvider';
import { useLocation } from 'react-router-dom';

interface HelpFeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpFeedbackModal({ open, onOpenChange }: HelpFeedbackModalProps) {
  const { session } = useAuth();
  const { selectedBrand } = useBrands();
  const location = useLocation();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Collect context about the user and their current state
      const contextInfo = {
        userEmail: session?.user?.email || 'Unknown',
        userId: session?.user?.id || 'Unknown',
        brandName: selectedBrand?.brand_name || 'No brand selected',
        brandId: selectedBrand?.id || 'None',
        currentPage: location.pathname,
        currentUrl: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      };

      // Format the message with context
      const formattedMessage = `
USER FEEDBACK/QUESTION:
${message}

---
CONTEXT INFORMATION:
- User Email: ${contextInfo.userEmail}
- User ID: ${contextInfo.userId}
- Brand: ${contextInfo.brandName} (${contextInfo.brandId})
- Current Page: ${contextInfo.currentPage}
- Full URL: ${contextInfo.currentUrl}
- Timestamp: ${contextInfo.timestamp}
- User Agent: ${contextInfo.userAgent}
      `.trim();

      const response = await fetch(
        `${import.meta.env.VITE_WORKERS_URL}/marketing-functions/submit_contact`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: session?.user?.email || 'Anonymous User',
            email: session?.user?.email || 'support@rainfactory.ai',
            company: selectedBrand?.brand_name || 'N/A',
            interest: 'support',
            message: formattedMessage,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setIsSubmitted(true);
      setMessage('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setMessage('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl shadow-sm">
              <Send className="h-5 w-5 text-blue-600" />
            </div>
            Help & Feedback
          </DialogTitle>
          <DialogDescription className="text-base">
            Have a question or feedback? Let us know and we'll get back to you as soon as possible.
          </DialogDescription>
        </DialogHeader>

        {isSubmitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center py-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="relative inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-100 to-emerald-200 mb-6 shadow-lg"
            >
              <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping" />
              <CheckCircle2 className="h-10 w-10 text-green-600 relative z-10" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-green-700 to-emerald-600 bg-clip-text text-transparent">
                Message Sent Successfully!
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Thank you for your feedback! We've received your message and will get back to you shortly.
              </p>
              <Button onClick={handleClose} variant="default">
                Close
              </Button>
            </motion.div>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="feedback-message" className="text-sm font-medium text-gray-700">
                Your Question or Feedback
              </Label>
              <Textarea
                id="feedback-message"
                placeholder="Please provide as much detail as possible about your question or feedback. Include any specific features, pages, or behaviors you're asking about..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={8}
                className="resize-none"
                required
              />
              <p className="text-xs text-gray-500">
                We'll automatically include information about your current session to help us assist you better.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 text-white"
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="flex items-center gap-2"
                  >
                    <Sparkles className="h-4 w-4" />
                    Sending...
                  </motion.div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Send Feedback
                  </div>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
